/**
 * response-code.ts
 * Copyright © 2016-2018, My Coverage Plan, Inc.
 * All rights reserved.
 */

/**
 * @author Taylor DH Smith <tdsmith@safetyweb.org>
 */

/**
 * The numeric codes returned with some server messages. (A copy of the backend
 * enum defined in com.mcp.hammurabi.server.ResponseCode)
 */
export enum ResponseCode
{
  META_AlreadyHandled = -1,
  OK = 0,
  UnspecifiedError = 1,
  LoginFailure = 2,
  InvalidRequest = 3,
  RequestExpired = 4,
  MissingData = 5,
  RequestFailed = 6,
  DuplicateEmail = 7,
  PermissionDenied = 8,
  Irretrievable = 9,
  SystemDown = 10,
  Maintenance = 11,
  DataCorruption = 12
}

// Declare that the maps may (should) have an additional property for defaults.
// Note this does nothing to accomplish the getOrDefault behavior; it simply
// defines a specific place to store the value. (TODO look into ES Proxy?)
type TextLookup = Map<ResponseCode, string> & {default? : string};

/**
 * The text to display to the user if a disconnection came through with the
 * stated reason.
 */
export const DISCONNECTION_TEXT : TextLookup = new Map([
  [
    ResponseCode.UnspecifiedError,
    "The server has disconnected for an unknown reason"
  ],
  [
    ResponseCode.LoginFailure,
    "Your credentials could not be authenticated."
  ],
  [
    ResponseCode.InvalidRequest,
    "The server declared your request invalid."
  ],
  [
    ResponseCode.RequestExpired,
    "Your request expired before it could be handled."
  ],
  [
    ResponseCode.MissingData,
    "You were disconnected because the server found a fault in the data."
  ],
  [
    ResponseCode.RequestFailed,
    "Your request could not be completed and the server chose to disconnect."
  ],
  [
    ResponseCode.DuplicateEmail,
    "Invalid request: that email is already in use!"
  ],
  [
    ResponseCode.PermissionDenied,
    "You didn't have the permission to make that request and were disconnected."
  ],
    // (ResponseCode.Irretrievable shouldn't cause disconnect)
  [
    ResponseCode.SystemDown,
    "The system is now offline."
  ],
  [
    ResponseCode.Maintenance,
    "Maintenance has begun on the application, requiring you to disconnect."
  ],
  [
    ResponseCode.DataCorruption,
    "A user submitted bad data that necessitated a disconnect."
  ],
]);
DISCONNECTION_TEXT.default =
  "The server has disconnected for an unknown reason";

/**
 * These "reasons" are modifier clauses designed to expand upon a base failure
 * description. The message should be a clause that fits in templates of the
 * form "... because ${reason}" or "${error label}: ${reason}".
 */
export const GENERIC_REASON_TEXT : TextLookup = new Map([
  [
    ResponseCode.UnspecifiedError,
    "an unspecified error occurred"
  ],
  [
    ResponseCode.LoginFailure,
    "your credentials were invalid"
  ],
  [
    ResponseCode.InvalidRequest,
    "the server could not interpret the request"
  ],
  [
    ResponseCode.RequestExpired,
    "the request was outdated or took too long"
  ],
  [
    ResponseCode.MissingData,
    "the server encountered missing data"
  ],
  [
    ResponseCode.RequestFailed,
    "the server could not complete the request successfully"
  ],
  [
    ResponseCode.DuplicateEmail,
    "that email is already in use"
  ],
  [
    ResponseCode.PermissionDenied,
    "you do not have permission to perform that action"
  ],
  [
    ResponseCode.Irretrievable,
    "the data would become irretrievable"
  ],
  [
    ResponseCode.SystemDown,
    "parts of the system are down or otherwise unavailable"
  ],
  [
    ResponseCode.Maintenance,
    "the system is actively undergoing maintenance"
  ],
  [
    ResponseCode.DataCorruption,
    "a user has submitted bad data that must be discarded"
  ],
]);
GENERIC_REASON_TEXT.default = "an unspecified error occurred";



// WEBPACK FOOTER //
// ./~/angular-router-loader/src!./app/server/messages/general/response-code.ts